import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { reportingService } from '../../../services/api';
import {
    AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from 'recharts';
import {
    C, FONT, PRESETS,
    getToday, getStartOfWeek, getEndOfWeek, getStartOfMonth, getStartOfYear,
    formatDateLabel,
    Sparkline, DownloadIcon, ExpandIcon, InfoIcon,
    EmptyState, SectionCard,
} from './_shared';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileCard from '../../../components/common/MobileCard';
import MobileHero from '../../../components/common/MobileHero';

/* ══════════════════════════════════════════════════════════════
   Editorial tokens — this page's unique visual character
   ══════════════════════════════════════════════════════════════ */
const ZONE_GAP = 48;           // editorial breathing room (Financial uses 32)
const CARD_RADIUS = 14;         // softer than Financial's 12
const CARD_PADDING = 24;        // roomier than Financial's 20

const zoneHeaderStyle = {
    fontFamily: FONT, fontSize: 22, fontWeight: 700,
    color: C.text, margin: 0, letterSpacing: -0.2,
};
const zoneSubStyle = {
    fontFamily: FONT, fontSize: 13, color: C.muted, marginTop: 4, marginBottom: 16,
};
const tabularNumeric = { fontVariantNumeric: 'tabular-nums' };

const editorialCard = {
    background: C.white,
    border: '1.5px solid ' + C.border,
    borderRadius: CARD_RADIUS,
    padding: CARD_PADDING,
};

/* ══════════════════════════════════════════════════════════════
   Formatters and band helpers
   ══════════════════════════════════════════════════════════════ */
const fmtNum = (v, digits = 0) => {
    if (v == null || Number.isNaN(Number(v))) return '0';
    return Number(v).toLocaleString(undefined, {
        minimumFractionDigits: digits, maximumFractionDigits: digits,
    });
};
const fmtMoney = (v, cur) => cur + fmtNum(v, 0);
const fmtMoneyPrecise = (v, cur) => cur + fmtNum(v, 2);
const fmtPct = (v, digits = 1) => (v == null ? '—' : `${v >= 0 ? '' : ''}${Number(v).toFixed(digits)}%`);

/** Margin band — green ≥40, amber 20–40, red <20 */
const marginBandColor = (pct) => {
    if (pct == null) return C.mutedLight;
    if (pct >= 40) return C.green;
    if (pct >= 20) return C.amber;
    return C.red;
};

/** Concentration band — green ≤70, amber 70–85, red >85 (higher = more risk) */
const concentrationBandColor = (pct) => {
    if (pct == null) return C.mutedLight;
    if (pct <= 70) return C.green;
    if (pct <= 85) return C.amber;
    return C.red;
};

/** Delta badge color — respects invert flag (higher = bad when invert) */
const deltaColor = (delta, invert = false) => {
    if (delta == null) return C.muted;
    const positive = delta > 0;
    if (invert) return positive ? C.red : C.green;
    return positive ? C.green : C.red;
};

/* ══════════════════════════════════════════════════════════════
   Icons specific to this page
   ══════════════════════════════════════════════════════════════ */
const AIIcon = ({ size = 14, color = C.green }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6z"/>
        <path d="M19 14v4"/><path d="M17 16h4"/>
    </svg>
);
const ArrowUp = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="18 15 12 9 6 15"/>
    </svg>
);
const ArrowDown = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9"/>
    </svg>
);
const CaretRight = ({ size = 14, rotated = false }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
         style={{ transform: rotated ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);
const CloseIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);
const SearchIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

/* ══════════════════════════════════════════════════════════════
   Reusable display components
   ══════════════════════════════════════════════════════════════ */

/** Delta pill: shows ↑X% or ↓X% with tight spacing. Use `invert` for KPIs where positive delta = bad. */
const DeltaBadge = ({ value, invert = false, unit = '%', digits = 1 }) => {
    if (value == null) return <span style={{ fontFamily: FONT, fontSize: 11, color: C.mutedLight }}>—</span>;
    const color = deltaColor(value, invert);
    const positive = value > 0;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 2,
            fontFamily: FONT, fontSize: 11, fontWeight: 700,
            color, ...tabularNumeric,
        }}>
            {positive ? <ArrowUp /> : value < 0 ? <ArrowDown /> : null}
            {Math.abs(value).toFixed(digits)}{unit}
        </span>
    );
};

/** Small (i) help affordance — hover/click to reveal explanation.
 *  Explicitly signals that each section has context available, so CFOs/owners
 *  aren't guessing what they're looking at. */
const HelpTooltip = ({ text, size = 12 }) => {
    const [open, setOpen] = useState(false);
    return (
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                onBlur={() => setOpen(false)}
                aria-label={text}
                aria-expanded={open}
                style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 18, height: 18, padding: 0,
                    background: open ? C.greenLight : 'transparent',
                    border: '1px solid ' + (open ? C.greenBorder : C.border),
                    borderRadius: '50%',
                    color: open ? C.greenDark : C.muted,
                    cursor: 'help',
                    transition: 'all 120ms ease',
                }}
            >
                <InfoIcon size={size} />
            </button>
            {open && (
                <span
                    role="tooltip"
                    style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: 8,
                        zIndex: 10,
                        background: C.text, color: C.white,
                        fontFamily: FONT, fontSize: 12, fontWeight: 500, lineHeight: 1.5,
                        padding: '10px 12px',
                        borderRadius: 8,
                        width: 260,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        pointerEvents: 'none',
                    }}
                >
                    {text}
                </span>
            )}
        </span>
    );
};

/** Full-width area sparkline used on KPI cards. Fills the bottom of the card
 *  so the visual weight matches the concentration gauge on the far right. */
const KpiSparklineArea = ({ data, color = C.green, height = 40 }) => {
    if (!data || data.length < 2) return null;
    const width = 140; // viewBox units — SVG stretches with width:100%
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const points = data.map((v, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((v - min) / range) * (height - 4) - 2;
        return [x, y];
    });
    const line = points.map(p => p.join(',')).join(' ');
    const area = `${points[0][0]},${height} ${line} ${points[points.length - 1][0]},${height}`;
    return (
        <svg
            width="100%" height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ display: 'block', marginTop: 6 }}
        >
            <polygon points={area} fill={color} fillOpacity="0.12" />
            <polyline
                points={line}
                fill="none" stroke={color}
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    );
};

/** Shimmer skeleton line for AI briefing loading. */
const ShimmerLine = ({ width = '100%', height = 12, delay = 0 }) => (
    <div style={{
        width, height, borderRadius: 4,
        background: `linear-gradient(90deg, ${C.bg} 0%, ${C.border} 50%, ${C.bg} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'pi-shimmer 1.6s ease-in-out infinite',
        animationDelay: `${delay}ms`,
        marginBottom: 8,
    }} />
);

/** AI Briefing card — editorial, restrained. */
const BriefingCard = ({ narrative, loading, t }) => (
    <div style={{
        ...editorialCard,
        marginBottom: 16,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <AIIcon size={14} color={C.green} />
            <span style={{
                fontFamily: FONT, fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 1.2, color: C.greenDark,
            }}>
                {t('product_briefing')}
            </span>
        </div>
        {loading ? (
            <div>
                <ShimmerLine width="96%" />
                <ShimmerLine width="88%" delay={100} />
                <ShimmerLine width="72%" delay={200} />
            </div>
        ) : narrative?.narrative ? (
            <>
                <div
                    style={{ fontFamily: FONT, fontSize: 15, fontWeight: 500, color: C.text, lineHeight: 1.65 }}
                    dangerouslySetInnerHTML={{
                        __html: narrative.narrative.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                    }}
                />
                <div style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight, marginTop: 14 }}>
                    {t('auto_generated')}
                    {narrative?.generated_at && ` · ${t('updated')} ${relativeTime(narrative.generated_at, t)}`}
                </div>
            </>
        ) : (
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted }}>
                {t('product_briefing_fallback')}
            </div>
        )}
    </div>
);

function relativeTime(iso, t) {
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const mins = Math.max(1, Math.round((now - then) / 60000));
        if (mins < 60) return `${mins} ${t('minutes_ago_short')}`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs} ${t('hours_ago_short')}`;
        const days = Math.round(hrs / 24);
        return `${days} ${t('days_ago_short')}`;
    } catch {
        return '';
    }
}

/** Concentration radial gauge — the page's signature visual.
 *  Fills the KPI card: large arc with a bold percentage inside,
 *  "from top N" subtitle inside the arc, and a single-line delta +
 *  diversity word beneath.
 *
 *  When the tenant has ≤ top_n products in the period, concentration is
 *  mathematically 100% and carries no signal — we render a neutral grey
 *  arc with a "only N products" note instead of a red warning + delta.
 */
const ConcentrationGauge = ({ sharePct, deltaPp, meta, t }) => {
    const startAngle = 200;
    const sweepTotal = 220;
    const radius = 54;
    const strokeWidth = 9;
    const cx = 80;
    const cy = 72;
    const pct = Math.max(0, Math.min(100, sharePct || 0));
    const filled = (pct / 100) * sweepTotal;

    const productCount = meta?.product_count ?? 0;
    const topN = meta?.top_n ?? 5;
    const insufficientVariety = productCount > 0 && productCount <= topN;

    const fillColor = insufficientVariety ? C.mutedLight : concentrationBandColor(sharePct);

    const polar = (angle, r) => {
        const rad = (angle - 90) * Math.PI / 180;
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };
    const arcPath = (sweep) => {
        const start = polar(startAngle, radius);
        const end = polar(startAngle + sweep, radius);
        const large = sweep > 180 ? 1 : 0;
        return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${large} 1 ${end.x} ${end.y}`;
    };

    const diversityLabel = deltaPp == null ? null
        : deltaPp > 0 ? t('menu_tightening_short')
        : deltaPp < 0 ? t('menu_loosening_short')
        : t('menu_holding_short');

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 4, marginTop: 4,
        }}>
            <svg
                width="100%" height={100}
                viewBox="0 0 160 100"
                preserveAspectRatio="xMidYMax meet"
                aria-hidden="true"
                style={{ display: 'block' }}
            >
                <path d={arcPath(sweepTotal)} fill="none" stroke={C.bg} strokeWidth={strokeWidth} strokeLinecap="round" />
                <path d={arcPath(filled)} fill="none" stroke={fillColor} strokeWidth={strokeWidth} strokeLinecap="round"
                      style={{ transition: 'all 400ms ease' }} />
                <text x={cx} y={cy - 4} textAnchor="middle"
                      fontFamily={FONT} fontSize="26" fontWeight="800"
                      fill={insufficientVariety ? C.muted : C.text}>
                    {fmtNum(pct, 0)}%
                </text>
                <text x={cx} y={cy + 13} textAnchor="middle"
                      fontFamily={FONT} fontSize="9" fontWeight="700" fill={C.mutedLight}
                      style={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {t('top_n_share').replace('{n}', meta?.top_n || 5)}
                </text>
            </svg>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: FONT, fontSize: 11,
                color: C.muted,
            }}>
                {insufficientVariety ? (
                    <span>{t('concentration_insufficient_variety').replace('{count}', productCount)}</span>
                ) : (
                    <>
                        <DeltaBadge value={deltaPp} invert={true} unit="pp" digits={1} />
                        {diversityLabel && (
                            <span>· {diversityLabel}</span>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/** KPI flip card — standard (non-concentration) version. */
const KpiCard = ({ kpi, description, sparkData, currency, unit, flipped, onFlip, t }) => {
    const value = kpi.value;
    const unitSuffix = unit || kpi.unit || '';
    const isMoney = !unitSuffix && typeof value === 'number' && kpi.name !== 'Units Sold';
    const showValue = isMoney ? fmtMoney(value, currency) : `${fmtNum(value, unitSuffix === '%' ? 1 : 0)}${unitSuffix}`;
    const invert = !!kpi.invert;

    return (
        <div style={{
            position: 'relative', perspective: 1000,
            height: 180, minWidth: 0,
        }}>
            <div style={{
                position: 'relative', width: '100%', height: '100%',
                transition: 'transform 450ms ease',
                transformStyle: 'preserve-3d',
                transform: flipped ? 'rotateY(180deg)' : 'none',
            }}>
                {/* Front face */}
                <button
                    onClick={onFlip}
                    aria-pressed={flipped}
                    aria-label={`${kpi.name}. ${t('click_to_see_description')}`}
                    style={{
                        position: 'absolute', inset: 0,
                        backfaceVisibility: 'hidden',
                        background: C.white,
                        border: '1.5px solid ' + C.border,
                        borderRadius: CARD_RADIUS,
                        padding: '16px 18px',
                        textAlign: 'left',
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        cursor: 'pointer',
                        fontFamily: FONT,
                    }}
                >
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: C.muted }}>
                                {t(kpiLabelKey(kpi.name))}
                            </div>
                            <span
                                aria-hidden="true"
                                title={t('click_to_see_description')}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 18, height: 18,
                                    border: '1px solid ' + C.border, borderRadius: '50%',
                                    color: C.mutedLight,
                                }}
                            >
                                <InfoIcon size={11} />
                            </span>
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: C.text, marginTop: 8, ...tabularNumeric }}>
                            {showValue}
                        </div>
                        <div style={{ marginTop: 6 }}>
                            <DeltaBadge value={kpi.change_percent} invert={invert} unit="%" digits={1} />
                        </div>
                    </div>
                    {sparkData && sparkData.length > 1 && (
                        <KpiSparklineArea data={sparkData} color={invert ? C.amber : C.green} />
                    )}
                </button>

                {/* Back face */}
                <div
                    aria-live="polite"
                    style={{
                        position: 'absolute', inset: 0,
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        background: C.white,
                        border: '1.5px solid ' + C.border,
                        borderRadius: CARD_RADIUS,
                        padding: '16px 18px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        fontFamily: FONT,
                    }}
                >
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: C.greenDark }}>
                            {t(kpiLabelKey(kpi.name))}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                            {description || t('no_description_available')}
                        </div>
                    </div>
                    <button
                        onClick={onFlip}
                        style={{
                            fontFamily: FONT, fontSize: 11, fontWeight: 600,
                            color: C.green, background: 'none', border: 'none',
                            cursor: 'pointer', textAlign: 'left', padding: 0, marginTop: 8,
                        }}
                    >
                        ← {t('back')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Concentration KPI card — custom front with radial gauge. */
const ConcentrationKpiCard = ({ kpi, description, flipped, onFlip, t }) => {
    const meta = kpi.meta || {};
    return (
        <div style={{ position: 'relative', perspective: 1000, height: 180, minWidth: 0 }}>
            <div style={{
                position: 'relative', width: '100%', height: '100%',
                transition: 'transform 450ms ease',
                transformStyle: 'preserve-3d',
                transform: flipped ? 'rotateY(180deg)' : 'none',
            }}>
                <button
                    onClick={onFlip}
                    aria-pressed={flipped}
                    aria-label={`${kpi.name}. ${t('click_to_see_description')}`}
                    style={{
                        position: 'absolute', inset: 0,
                        backfaceVisibility: 'hidden',
                        background: C.white,
                        border: '1.5px solid ' + C.border,
                        borderRadius: CARD_RADIUS,
                        padding: '12px 18px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: FONT,
                        display: 'flex', flexDirection: 'column',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: C.muted }}>
                            {t('kpi_revenue_concentration')}
                        </div>
                        <span
                            aria-hidden="true"
                            title={t('click_to_see_description')}
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 18, height: 18,
                                border: '1px solid ' + C.border, borderRadius: '50%',
                                color: C.mutedLight,
                            }}
                        >
                            <InfoIcon size={11} />
                        </span>
                    </div>
                    <ConcentrationGauge
                        sharePct={kpi.value}
                        deltaPp={kpi.change_absolute}
                        meta={meta}
                        t={t}
                    />
                </button>
                <div
                    aria-live="polite"
                    style={{
                        position: 'absolute', inset: 0,
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        background: C.white,
                        border: '1.5px solid ' + C.border,
                        borderRadius: CARD_RADIUS,
                        padding: '16px 18px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        fontFamily: FONT,
                    }}
                >
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: C.greenDark }}>
                            {t('kpi_revenue_concentration')}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                            {description || t('concentration_description_fallback')}
                        </div>
                    </div>
                    <button
                        onClick={onFlip}
                        style={{
                            fontFamily: FONT, fontSize: 11, fontWeight: 600,
                            color: C.green, background: 'none', border: 'none',
                            cursor: 'pointer', textAlign: 'left', padding: 0, marginTop: 8,
                        }}
                    >
                        ← {t('back')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Map KPI name (from backend) to a translation key. Stable across locales. */
function kpiLabelKey(name) {
    switch (name) {
        case 'Product Revenue': return 'kpi_product_revenue';
        case 'Gross Margin': return 'kpi_gross_margin';
        case 'Units Sold': return 'kpi_units_sold';
        case 'Revenue Concentration': return 'kpi_revenue_concentration';
        default: return name;
    }
}

/** Map KPI name to the description key in the narrative's kpi_descriptions dict. */
function kpiDescriptionKey(name) {
    switch (name) {
        case 'Product Revenue': return 'product_revenue';
        case 'Gross Margin': return 'gross_margin';
        case 'Units Sold': return 'units_sold';
        case 'Revenue Concentration': return 'revenue_concentration';
        default: return name.toLowerCase().replace(/\s+/g, '_');
    }
}

/* ══════════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════════ */
export default function ProductIntelligencePage({ parentDates } = {}) {
    const { getCurrency } = useAuth();
    const currency = getCurrency ? getCurrency() : '';
    const { t } = useLanguage();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    /* ── State ─────────────────────────────────────────────── */
    const [preset, setPreset] = useState('week');
    const [customStart, setCustomStart] = useState(getToday);
    const [customEnd, setCustomEnd] = useState(getToday);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [narrative, setNarrative] = useState(null);
    const [narrativeLoading, setNarrativeLoading] = useState(true);
    const [flippedCards, setFlippedCards] = useState({});
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [fullListModalOpen, setFullListModalOpen] = useState(false);
    const [productDetail, setProductDetail] = useState(null); // { open, code, data, loading }
    const [error, setError] = useState(null);
    const flipTimers = useRef({});

    /* ── Date range ────────────────────────────────────────── */
    const internalRange = useMemo(() => {
        switch (preset) {
            case 'today':  return { start: getToday(), end: getToday() };
            case 'week':   return { start: getStartOfWeek(), end: getEndOfWeek() };
            case 'month':  return { start: getStartOfMonth(), end: getToday() };
            case 'year':   return { start: getStartOfYear(), end: getToday() };
            case 'custom': return { start: customStart, end: customEnd };
            default:       return { start: getToday(), end: getToday() };
        }
    }, [preset, customStart, customEnd]);
    const dateRange = parentDates || internalRange;

    /* ── Fetchers ──────────────────────────────────────────── */
    const fetchData = useCallback(async () => {
        if (data) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const result = await reportingService.getProductsV2(dateRange.start, dateRange.end);
            setData(result);
        } catch (err) {
            console.error('Product intelligence fetch error:', err);
            setError('fetch_failed');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [dateRange.start, dateRange.end, data]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setNarrativeLoading(true);
            try {
                const res = await reportingService.getProductNarrative(dateRange.start, dateRange.end);
                if (!cancelled) setNarrative(res);
            } catch (e) {
                if (!cancelled) setNarrative(null);
            } finally {
                if (!cancelled) setNarrativeLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [dateRange.start, dateRange.end]);

    useEffect(() => {
        fetchData();
        // fetchData intentionally omitted from deps — it would loop on every data change
    }, [dateRange.start, dateRange.end]);

    /* ── KPI flip ──────────────────────────────────────────── */
    const toggleFlip = (idx) => {
        const wasFlipped = flippedCards[idx];
        setFlippedCards(prev => ({ ...prev, [idx]: !prev[idx] }));
        if (flipTimers.current[idx]) clearTimeout(flipTimers.current[idx]);
        if (!wasFlipped) {
            flipTimers.current[idx] = setTimeout(() => {
                setFlippedCards(prev => ({ ...prev, [idx]: false }));
            }, 5000);
        }
    };

    /* ── Product drill-down modal ──────────────────────────── */
    const openProductDetail = async (code) => {
        setProductDetail({ open: true, code, data: null, loading: true, error: null });
        try {
            const res = await reportingService.getProductDetail(code, dateRange.start, dateRange.end);
            setProductDetail(prev => prev && prev.code === code
                ? { ...prev, data: res, loading: false }
                : prev);
        } catch (e) {
            setProductDetail(prev => prev && prev.code === code
                ? { ...prev, loading: false, error: 'fetch_failed' }
                : prev);
        }
    };
    const closeProductDetail = () => setProductDetail(null);

    /* ── Esc handling for modals ───────────────────────────── */
    useEffect(() => {
        const handler = (e) => {
            if (e.key !== 'Escape') return;
            if (productDetail?.open) setProductDetail(null);
            else if (fullListModalOpen) setFullListModalOpen(false);
            else if (categoryModalOpen) setCategoryModalOpen(false);
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [productDetail, fullListModalOpen, categoryModalOpen]);

    /* ── Derived data ──────────────────────────────────────── */
    const sections = data?.sections || {};
    const kpis = data?.kpis || [];
    const kpiDescriptions = narrative?.kpi_descriptions || {};
    const topContributors = sections.top_contributors || [];
    const fallingBehind = sections.falling_behind || [];
    const categoryMovement = sections.category_movement || [];
    const velocity = sections.velocity_watchlist || { accelerating: [], decelerating: [] };
    const productsFull = sections.products_full || [];

    const concMeta = kpis.find(k => k.name === 'Revenue Concentration')?.meta || {};

    /* ── Loading state ─────────────────────────────────────── */
    if (loading && !data) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted, padding: '60px 0', textAlign: 'center' }}>
                {t('loading')}...
            </div>
        );
    }

    /* ── Page-level empty state ────────────────────────────── */
    const noData = !loading && (!data || (productsFull.length === 0 && topContributors.length === 0));
    if (noData) {
        return (
            <div>
                {!parentDates && (
                    <PageHeader
                        preset={preset} setPreset={setPreset}
                        customStart={customStart} setCustomStart={setCustomStart}
                        customEnd={customEnd} setCustomEnd={setCustomEnd}
                        t={t}
                    />
                )}
                <div style={{ ...editorialCard, textAlign: 'center', padding: '60px 20px' }}>
                    <EmptyState title={t('no_product_sales_in_period')} />
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight, marginTop: 8 }}>
                        {t('try_wider_date_range')}
                    </div>
                </div>
            </div>
        );
    }

    /* ── Render ────────────────────────────────────────────── */
    return (
        <div>
            {/* Shimmer keyframes — scoped via <style> */}
            <style>{`
                @keyframes pi-shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                @media (prefers-reduced-motion: reduce) {
                    @keyframes pi-shimmer { 0%,100% { background-position: 0 0; } }
                }
                .pi-row-button:hover { background: ${C.bg}; }
                .pi-row-button:focus-visible { outline: 2px solid ${C.green}; outline-offset: 2px; }
                .pi-product-btn { all: unset; cursor: pointer; font-family: ${FONT}; }
                .pi-product-btn:hover { text-decoration: underline; }
                .pi-product-btn:focus-visible { outline: 2px solid ${C.green}; outline-offset: 2px; border-radius: 2px; }
            `}</style>

            {!parentDates && (
                <PageHeader
                    preset={preset} setPreset={setPreset}
                    customStart={customStart} setCustomStart={setCustomStart}
                    customEnd={customEnd} setCustomEnd={setCustomEnd}
                    dateRange={dateRange}
                    refreshing={refreshing}
                    t={t}
                />
            )}

            {/* ═════════ ZONE 1 — At a glance ═════════ */}
            <BriefingCard narrative={narrative} loading={narrativeLoading} t={t} />

            {isMobile ? (
                kpis.length > 0 ? (() => {
                    const fmtKpiValue = (k) => {
                        if (k.name === 'Revenue Concentration') return fmtNum(k.value, 0) + '%';
                        const unitSuffix = k.unit || '';
                        const isMoney = !unitSuffix && typeof k.value === 'number' && k.name !== 'Units Sold';
                        if (isMoney) return fmtMoney(k.value, currency);
                        return `${fmtNum(k.value, unitSuffix === '%' ? 1 : 0)}${unitSuffix}`;
                    };
                    const headline = kpis.find(k => k.name === 'Product Revenue') || kpis[0];
                    const rest = kpis.filter(k => k !== headline).slice(0, 4);
                    const hc = headline.change_percent || 0;
                    return (
                        <div style={{ marginBottom: 24 }}>
                            <MobileHero
                                label={t(kpiLabelKey(headline.name))}
                                value={fmtKpiValue(headline)}
                                meta={`${hc >= 0 ? '↑' : '↓'} ${Math.abs(hc).toFixed(1)}%`}
                                stats={rest.map(k => ({
                                    label: t(kpiLabelKey(k.name)),
                                    value: fmtKpiValue(k),
                                }))}
                            />
                        </div>
                    );
                })() : null
            ) : (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 16,
                marginBottom: ZONE_GAP,
            }}>
                {kpis.map((kpi, idx) => {
                    const flipped = !!flippedCards[idx];
                    const descKey = kpiDescriptionKey(kpi.name);
                    const description = kpiDescriptions[descKey];
                    if (kpi.name === 'Revenue Concentration') {
                        return (
                            <ConcentrationKpiCard
                                key={idx}
                                kpi={kpi}
                                description={description}
                                flipped={flipped}
                                onFlip={() => toggleFlip(idx)}
                                t={t}
                            />
                        );
                    }
                    const trends = data?.kpi_trends || {};
                    const sparkData = kpi.name === 'Product Revenue' ? trends.revenue
                        : kpi.name === 'Gross Margin' ? trends.margin_pct
                        : kpi.name === 'Units Sold' ? trends.units
                        : null;
                    return (
                        <KpiCard
                            key={idx}
                            kpi={kpi}
                            description={description}
                            sparkData={sparkData}
                            currency={currency}
                            flipped={flipped}
                            onFlip={() => toggleFlip(idx)}
                            t={t}
                        />
                    );
                })}
            </div>
            )}

            {/* ═════════ ZONE 2 — Pulling their weight / Falling behind ═════════ */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: ZONE_GAP }}>
                {/* Left — Pulling their weight */}
                <div style={editorialCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h3 style={zoneHeaderStyle}>{t('pulling_their_weight')}</h3>
                        <HelpTooltip text={t('help_pulling_their_weight')} />
                    </div>
                    <div style={zoneSubStyle}>
                        {topContributors.length > 0 && concMeta.top_n
                            ? t('top_n_equals_pct')
                                .replace('{n}', concMeta.top_n)
                                .replace('{pct}', fmtNum(kpis.find(k => k.name === 'Revenue Concentration')?.value || 0, 0))
                            : t('top_contributors_subtitle')}
                    </div>
                    {topContributors.length === 0 ? (
                        <EmptyState title={t('no_products_sold_in_period')} />
                    ) : (
                        <div>
                            {topContributors.map((p, i) => (
                                <TopContributorRow
                                    key={p.product_code || i}
                                    product={p}
                                    currency={currency}
                                    onOpen={() => openProductDetail(p.product_code)}
                                    t={t}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Right — Falling behind */}
                <div style={editorialCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h3 style={zoneHeaderStyle}>{t('falling_behind')}</h3>
                        <HelpTooltip text={t('help_falling_behind')} />
                    </div>
                    <div style={zoneSubStyle}>{t('falling_behind_subtitle')}</div>
                    {fallingBehind.length === 0 ? (
                        <div style={{
                            padding: '30px 8px', textAlign: 'center',
                            fontFamily: FONT, fontSize: 13, color: C.muted, lineHeight: 1.55,
                        }}>
                            {t('no_products_lost_ground')}
                        </div>
                    ) : (
                        <div>
                            {fallingBehind.map((p, i) => (
                                <FallingBehindRow
                                    key={p.product_code || i}
                                    product={p}
                                    currency={currency}
                                    onOpen={() => openProductDetail(p.product_code)}
                                    t={t}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ═════════ ZONE 3 — What's changing ═════════ */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: ZONE_GAP }}>
                {/* Left — Category movement */}
                <div style={editorialCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <h3 style={zoneHeaderStyle}>{t('how_categories_are_moving')}</h3>
                                <HelpTooltip text={t('help_category_movement')} />
                            </div>
                            <div style={zoneSubStyle}>{t('category_movement_subtitle')}</div>
                        </div>
                        {categoryMovement.length > 0 && (
                            <button
                                onClick={() => setCategoryModalOpen(true)}
                                aria-label={t('expand_chart')}
                                title={t('expand_chart')}
                                style={{
                                    background: 'none', border: '1px solid ' + C.border, borderRadius: 6,
                                    cursor: 'pointer', padding: '4px 6px',
                                    display: 'flex', alignItems: 'center', color: C.muted,
                                }}
                            >
                                <ExpandIcon />
                            </button>
                        )}
                    </div>
                    {categoryMovement.length === 0 ? (
                        <EmptyState title={t('no_category_data')} />
                    ) : (
                        <div>
                            {categoryMovement.map((c, i) => (
                                <CategoryRow
                                    key={c.category || i}
                                    category={c}
                                    expanded={expandedCategories.has(c.category)}
                                    onToggle={() => {
                                        setExpandedCategories(prev => {
                                            const next = new Set(prev);
                                            if (next.has(c.category)) next.delete(c.category);
                                            else next.add(c.category);
                                            return next;
                                        });
                                    }}
                                    t={t}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Right — Momentum shifts */}
                <div style={editorialCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h3 style={zoneHeaderStyle}>{t('momentum_shifts')}</h3>
                        <HelpTooltip text={t('help_momentum_shifts')} />
                    </div>
                    <div style={zoneSubStyle}>{t('momentum_subtitle')}</div>
                    {velocity.accelerating.length === 0 && velocity.decelerating.length === 0 ? (
                        <div style={{
                            padding: '30px 8px', textAlign: 'center',
                            fontFamily: FONT, fontSize: 13, color: C.muted, lineHeight: 1.55,
                        }}>
                            {t('momentum_not_enough_history')}
                        </div>
                    ) : (
                        <>
                            <VelocityGroup
                                title={t('gaining_momentum')}
                                products={velocity.accelerating}
                                direction="up"
                                onOpen={openProductDetail}
                                t={t}
                            />
                            {velocity.accelerating.length > 0 && velocity.decelerating.length > 0 && (
                                <div style={{ height: 1, background: C.border, margin: '14px 0' }} />
                            )}
                            <VelocityGroup
                                title={t('losing_momentum')}
                                products={velocity.decelerating}
                                direction="down"
                                onOpen={openProductDetail}
                                t={t}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* ═════════ ZONE 4 — The full menu ═════════ */}
            <div style={{ ...editorialCard, marginBottom: ZONE_GAP }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h3 style={zoneHeaderStyle}>{t('the_full_menu')}</h3>
                            <HelpTooltip text={t('help_full_menu')} />
                        </div>
                        <div style={zoneSubStyle}>{t('full_menu_subtitle')}</div>
                    </div>
                </div>

                {productsFull.length === 0 ? (
                    <EmptyState title={t('no_products_sold_in_period')} />
                ) : (
                    <>
                        {isMobile ? (
                            <div>
                                {productsFull.slice(0, 10).map(p => {
                                    const delta = p.revenue_delta_pct || 0;
                                    return (
                                        <MobileCard
                                            key={p.product_code}
                                            title={p.product_name}
                                            amount={fmtMoney(p.revenue, currency)}
                                            subtitle={`${p.category || '—'} · ${fmtNum(p.units, 0)} ${t('units')}`}
                                            status={{
                                                plain: true,
                                                tone: delta >= 0 ? 'green' : 'red',
                                                icon: delta >= 0 ? '↑ ' : '↓ ',
                                                label: `${Math.abs(delta).toFixed(1)}%`,
                                            }}
                                            collapsible={false}
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <FullMenuTable
                                products={productsFull.slice(0, 10)}
                                currency={currency}
                                onOpen={openProductDetail}
                                t={t}
                            />
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <button
                                onClick={() => setFullListModalOpen(true)}
                                style={{
                                    fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                    color: C.green, background: 'none',
                                    border: '1.5px solid ' + C.green, borderRadius: 8,
                                    padding: '8px 14px', cursor: 'pointer',
                                    transition: 'all 150ms ease',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = C.greenLight; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                {t('see_all_products')} →
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* ═════════ Modals ═════════ */}
            {categoryModalOpen && (
                <CategoryChartModal
                    dateRange={dateRange}
                    categories={categoryMovement}
                    currency={currency}
                    onClose={() => setCategoryModalOpen(false)}
                    t={t}
                />
            )}
            {fullListModalOpen && (
                <FullProductsModal
                    products={productsFull}
                    currency={currency}
                    onClose={() => setFullListModalOpen(false)}
                    onOpenProduct={openProductDetail}
                    t={t}
                />
            )}
            {productDetail?.open && (
                <ProductDetailModal
                    state={productDetail}
                    currency={currency}
                    onClose={closeProductDetail}
                    onNavigateToProduct={(code) => {
                        closeProductDetail();
                        navigate('/portal/products');
                    }}
                    t={t}
                />
            )}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   Header sub-component
   ══════════════════════════════════════════════════════════════ */
function PageHeader({ preset, setPreset, customStart, setCustomStart, customEnd, setCustomEnd, refreshing, t }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            marginBottom: 24, flexWrap: 'wrap', gap: 12,
        }}>
            <div>
                <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>
                    {t('product_intelligence')}
                </h2>
                <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginTop: 4 }}>
                    {t('product_intelligence_subtitle')}
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {refreshing && (
                    <div style={{ fontFamily: FONT, fontSize: 11, color: C.mutedLight }}>
                        {t('updating')}…
                    </div>
                )}
                <div style={{
                    display: 'inline-flex', background: C.bg, borderRadius: 8,
                    border: '1.5px solid ' + C.border, overflow: 'hidden',
                }}>
                    {PRESETS.map(p => (
                        <button
                            key={p.key}
                            onClick={() => setPreset(p.key)}
                            style={{
                                fontFamily: FONT, fontSize: 12, fontWeight: 600,
                                padding: '7px 14px', border: 'none', cursor: 'pointer',
                                background: preset === p.key ? C.green : 'transparent',
                                color: preset === p.key ? C.white : C.muted,
                                transition: 'all 0.15s',
                            }}
                        >{t(p.label)}</button>
                    ))}
                </div>
                {preset === 'custom' && (
                    <>
                        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                            style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none' }}
                        />
                        <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{t('to')}</span>
                        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                            style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none' }}
                        />
                    </>
                )}
                <button
                    onClick={() => window.print()}
                    style={{
                        fontFamily: FONT, fontSize: 12, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 16px', border: '1.5px solid ' + C.border, borderRadius: 8,
                        background: C.white, color: C.text, cursor: 'pointer',
                    }}
                >
                    <DownloadIcon /> {t('export_pdf')}
                </button>
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   Zone 2 rows
   ══════════════════════════════════════════════════════════════ */
function TopContributorRow({ product, currency, onOpen, t }) {
    const marginColor = marginBandColor(product.margin_pct);
    const barPct = Math.max(0.5, Math.min(100, product.pct_of_total || 0));
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 120px',
            alignItems: 'center', gap: 16,
            padding: '12px 0',
            borderBottom: '1px solid ' + C.border,
        }}>
            <div style={{ minWidth: 0 }}>
                <button
                    onClick={onOpen}
                    className="pi-product-btn"
                    style={{ fontSize: 14, fontWeight: 600, color: C.text, display: 'block' }}
                    aria-label={t('product_open_detail').replace('{name}', product.product_name)}
                >
                    {product.product_name}
                </button>
                <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {product.category || '—'}
                </div>
                <div style={{ height: 10, background: C.bg, borderRadius: 5, overflow: 'hidden', marginTop: 8 }}>
                    <div style={{
                        height: '100%',
                        width: barPct + '%',
                        background: marginColor,
                        borderRadius: 5,
                        transition: 'width 300ms ease',
                    }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight }}>
                        {fmtNum(product.pct_of_total, 1)}% {t('of_revenue')}
                    </span>
                    <span style={{ fontFamily: FONT, fontSize: 10, color: marginColor, fontWeight: 700 }}>
                        {fmtNum(product.margin_pct, 1)}% {t('margin')}
                    </span>
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.text, ...tabularNumeric }}>
                    {fmtMoney(product.revenue, currency)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                    <DeltaBadge value={product.revenue_delta_pct} unit="%" digits={1} />
                    <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, ...tabularNumeric }}>
                        · {fmtNum(product.units, 0)} {t('units')}
                    </span>
                </div>
            </div>
        </div>
    );
}

function FallingBehindRow({ product, currency, onOpen, t }) {
    return (
        <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto',
            alignItems: 'center', gap: 16,
            padding: '12px 0',
            borderBottom: '1px solid ' + C.border,
        }}>
            <div style={{ minWidth: 0 }}>
                <button
                    onClick={onOpen}
                    className="pi-product-btn"
                    style={{ fontSize: 14, fontWeight: 600, color: C.text, display: 'block' }}
                    aria-label={t('product_open_detail').replace('{name}', product.product_name)}
                >
                    {product.product_name}
                </button>
                <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {product.category || '—'}
                </div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 8, ...tabularNumeric }}>
                    {fmtMoney(product.previous_revenue, currency)} → <span style={{ color: C.text, fontWeight: 600 }}>{fmtMoney(product.current_revenue, currency)}</span>
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: C.red, ...tabularNumeric }}>
                    <DeltaBadge value={product.revenue_delta_pct} unit="%" digits={1} />
                </div>
                <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 4, ...tabularNumeric }}>
                    {product.units_delta >= 0 ? '+' : ''}{fmtNum(product.units_delta, 0)} {t('units')}
                </div>
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   Zone 3 components
   ══════════════════════════════════════════════════════════════ */
function CategoryRow({ category, expanded, onToggle, t }) {
    const hasSubs = (category.subcategories || []).length > 0;
    const barPct = Math.max(0.5, Math.min(100, category.pct_of_total || 0));
    return (
        <div style={{ borderBottom: '1px solid ' + C.border }}>
            <button
                onClick={hasSubs ? onToggle : undefined}
                className="pi-row-button"
                disabled={!hasSubs}
                style={{
                    display: 'grid',
                    gridTemplateColumns: '20px 1fr auto',
                    alignItems: 'center', gap: 12,
                    width: '100%',
                    padding: '12px 4px',
                    background: 'none', border: 'none',
                    textAlign: 'left', cursor: hasSubs ? 'pointer' : 'default',
                    fontFamily: FONT, color: C.text,
                }}
            >
                <span style={{ color: hasSubs ? C.muted : 'transparent' }}>
                    <CaretRight rotated={expanded} />
                </span>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{category.category}</span>
                        <span style={{ fontSize: 11, color: C.muted }}>
                            {category.product_count} {t('products')}
                        </span>
                    </div>
                    <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
                        <div style={{
                            height: '100%', width: barPct + '%', background: C.green, borderRadius: 4,
                            transition: 'width 300ms ease',
                        }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: C.mutedLight, ...tabularNumeric }}>
                            {fmtNum(category.pct_of_total, 1)}% {t('of_revenue')}
                        </span>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <DeltaBadge value={category.revenue_delta_pct} unit="%" digits={1} />
                </div>
            </button>

            {expanded && hasSubs && (
                <div style={{ background: C.bg, padding: '8px 12px 12px 34px' }}>
                    {category.subcategories.map((s, i) => (
                        <div key={s.subcategory || i} style={{
                            display: 'grid', gridTemplateColumns: '1fr auto', gap: 12,
                            padding: '8px 0',
                            borderBottom: i < category.subcategories.length - 1 ? '1px dashed ' + C.border : 'none',
                        }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                    <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: C.text }}>
                                        {s.subcategory}
                                    </span>
                                    <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>
                                        {s.product_count} {t('products')}
                                    </span>
                                </div>
                                <div style={{ height: 6, background: C.white, borderRadius: 3, overflow: 'hidden', marginTop: 4 }}>
                                    <div style={{
                                        height: '100%',
                                        width: Math.max(0.5, Math.min(100, s.pct_of_total || 0)) + '%',
                                        background: C.greenBorder, borderRadius: 3,
                                    }} />
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <DeltaBadge value={s.revenue_delta_pct} unit="%" digits={1} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function VelocityGroup({ title, products, direction, onOpen, t }) {
    const color = direction === 'up' ? C.green : C.amber;
    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
                fontFamily: FONT, fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 1, color: C.muted,
            }}>
                <span style={{ color }}>
                    {direction === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                </span>
                {title}
            </div>
            {products.length === 0 ? (
                <div style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight, padding: '6px 0 10px' }}>
                    — {t('none')} —
                </div>
            ) : (
                products.map((p, i) => {
                    const pctDelta = p.delta_7d_vs_30d == null ? null : p.delta_7d_vs_30d * 100;
                    return (
                        <div key={p.product_code || i} style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 70px auto',
                            alignItems: 'center', gap: 12,
                            padding: '8px 0',
                            borderBottom: i < products.length - 1 ? '1px dashed ' + C.border : 'none',
                        }}>
                            <div style={{ minWidth: 0 }}>
                                <button
                                    onClick={() => onOpen(p.product_code)}
                                    className="pi-product-btn"
                                    style={{ fontSize: 13, fontWeight: 600, color: C.text, display: 'block' }}
                                    aria-label={t('product_open_detail').replace('{name}', p.product_name)}
                                >
                                    {p.product_name}
                                </button>
                                <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 2, ...tabularNumeric }}>
                                    {p.sales_last_7d} / 7d · {p.sales_last_30d} / 30d
                                </div>
                            </div>
                            <Sparkline data={p.sparkline_30d || []} color={color} width={70} height={20} />
                            <div style={{ textAlign: 'right', color }}>
                                <DeltaBadge value={pctDelta} invert={direction === 'down'} unit="%" digits={0} />
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   Zone 4 — full menu inline table
   ══════════════════════════════════════════════════════════════ */
function FullMenuTable({ products, currency, onOpen, t }) {
    return (
        <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                <thead>
                    <tr style={{ borderBottom: '1.5px solid ' + C.border }}>
                        <th scope="col" style={thStyle}>{t('table_product')}</th>
                        <th scope="col" style={thStyle}>{t('table_category')}</th>
                        <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{t('table_units')}</th>
                        <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{t('table_revenue')}</th>
                        <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>Δ%</th>
                        <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{t('table_margin')}</th>
                        <th scope="col" style={{ ...thStyle, textAlign: 'right' }}>{t('table_trend_30d')}</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((p, i) => (
                        <tr key={p.product_code || i}
                            style={{
                                borderBottom: '1px solid ' + C.border,
                                transition: 'background 100ms',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                            <td style={tdStyle}>
                                <button
                                    onClick={() => onOpen(p.product_code)}
                                    className="pi-product-btn"
                                    style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                                    aria-label={t('product_open_detail').replace('{name}', p.product_name)}
                                >
                                    {p.product_name}
                                </button>
                            </td>
                            <td style={{ ...tdStyle, fontSize: 12, color: C.muted }}>{p.category || '—'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', ...tabularNumeric }}>{fmtNum(p.units, 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, ...tabularNumeric }}>{fmtMoney(p.revenue, currency)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                <DeltaBadge value={p.revenue_delta_pct} unit="%" digits={1} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: marginBandColor(p.margin_pct), fontWeight: 600, ...tabularNumeric }}>
                                {fmtNum(p.margin_pct, 1)}%
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Sparkline data={p.sparkline_30d || []} width={60} height={20} color={marginBandColor(p.margin_pct)} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
const thStyle = {
    fontFamily: FONT, fontSize: 11, fontWeight: 700,
    color: C.muted, textAlign: 'left',
    textTransform: 'uppercase', letterSpacing: 0.8,
    padding: '10px 12px',
};
const tdStyle = {
    fontFamily: FONT, fontSize: 13, color: C.text,
    padding: '12px',
    verticalAlign: 'middle',
};

/* ══════════════════════════════════════════════════════════════
   Modals
   ══════════════════════════════════════════════════════════════ */

const modalScrimStyle = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
    animation: 'pi-modal-fade 120ms ease-out',
};
const modalCardStyle = {
    background: C.white, borderRadius: CARD_RADIUS,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    display: 'flex', flexDirection: 'column',
    maxHeight: '85vh', overflow: 'hidden',
    animation: 'pi-modal-pop 180ms ease-out',
};
const modalHeaderStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid ' + C.border,
    fontFamily: FONT,
};
const modalCloseBtnStyle = {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 4, color: C.muted, borderRadius: 4,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

function ModalShell({ titleNode, widthPx, onClose, children }) {
    return (
        <div
            role="dialog" aria-modal="true"
            style={modalScrimStyle}
            onClick={onClose}
        >
            <style>{`
                @keyframes pi-modal-fade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes pi-modal-pop { from { opacity: 0; transform: scale(0.96) } to { opacity: 1; transform: scale(1) } }
                @media (prefers-reduced-motion: reduce) {
                    @keyframes pi-modal-fade { from, to { opacity: 1 } }
                    @keyframes pi-modal-pop  { from, to { opacity: 1; transform: none } }
                }
            `}</style>
            <div
                className="portal-modal-card"
                onClick={e => e.stopPropagation()}
                style={{ ...modalCardStyle, width: widthPx, maxWidth: 'calc(100vw - 40px)' }}
            >
                <div style={modalHeaderStyle}>
                    {titleNode}
                    <button onClick={onClose} aria-label="Close" style={modalCloseBtnStyle}>
                        <CloseIcon />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

/* ───── Category chart modal ───── */
function CategoryChartModal({ categories, currency, onClose, t }) {
    const data = (categories || []).map(c => ({
        name: c.category,
        revenue: Number(c.revenue || 0),
        delta: c.revenue_delta_pct || 0,
    }));
    return (
        <ModalShell
            widthPx={720}
            onClose={onClose}
            titleNode={
                <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                        {t('how_categories_are_moving')}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                        {t('category_movement_subtitle')}
                    </div>
                </div>
            }
        >
            <div style={{ padding: 20, overflowY: 'auto' }}>
                {data.length === 0 ? (
                    <EmptyState title={t('no_category_data')} />
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                            <XAxis
                                dataKey="name"
                                tick={{ fontFamily: FONT, fontSize: 11, fill: C.muted }}
                                angle={-30}
                                textAnchor="end"
                                interval={0}
                                height={70}
                                axisLine={false} tickLine={false}
                            />
                            <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                            <Tooltip
                                contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                formatter={(v) => [fmtMoneyPrecise(v, currency), t('table_revenue')]}
                            />
                            <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                                {data.map((d, i) => (
                                    <Cell key={i} fill={d.delta >= 0 ? C.green : C.amber} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </ModalShell>
    );
}

/* ───── Full products modal ───── */
function FullProductsModal({ products, currency, onClose, onOpenProduct, t }) {
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [sortBy, setSortBy] = useState('revenue');
    const [sortDir, setSortDir] = useState('desc');

    const categories = useMemo(() => {
        const set = new Set();
        products.forEach(p => { if (p.category) set.add(p.category); });
        return Array.from(set).sort();
    }, [products]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return products.filter(p => {
            if (q && !(p.product_name || '').toLowerCase().includes(q)
                 && !(p.product_code || '').toLowerCase().includes(q)) return false;
            if (categoryFilter && p.category !== categoryFilter) return false;
            return true;
        });
    }, [products, search, categoryFilter]);

    const sorted = useMemo(() => {
        const arr = [...filtered];
        const dir = sortDir === 'desc' ? -1 : 1;
        arr.sort((a, b) => {
            const av = a[sortBy] ?? 0;
            const bv = b[sortBy] ?? 0;
            if (typeof av === 'string') return av.localeCompare(bv) * dir;
            return (av - bv) * dir;
        });
        return arr;
    }, [filtered, sortBy, sortDir]);

    const toggleSort = (col) => {
        if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(col); setSortDir('desc'); }
    };

    const exportCsv = () => {
        const headers = ['product_code', 'product_name', 'category', 'subcategory', 'units', 'revenue', 'revenue_delta_pct', 'margin_pct'];
        const rows = sorted.map(p => headers.map(h => JSON.stringify(p[h] ?? '')).join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `product-intelligence-full-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const sortArrow = (col) => sortBy === col ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

    return (
        <ModalShell
            widthPx={960}
            onClose={onClose}
            titleNode={
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                    {t('the_full_menu')} ({sorted.length})
                </div>
            }
        >
            <div style={{ padding: 16, borderBottom: '1px solid ' + C.border, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    border: '1.5px solid ' + C.border, borderRadius: 8, padding: '6px 10px',
                    flex: 1, minWidth: 200,
                }}>
                    <span style={{ color: C.muted }}><SearchIcon /></span>
                    <input
                        type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder={t('search_products')}
                        style={{ border: 'none', outline: 'none', flex: 1, fontFamily: FONT, fontSize: 13 }}
                    />
                </div>
                <select
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                    style={{
                        fontFamily: FONT, fontSize: 13,
                        border: '1.5px solid ' + C.border, borderRadius: 8,
                        padding: '7px 10px', background: C.white, color: C.text,
                    }}
                >
                    <option value="">{t('all_categories')}</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                    onClick={exportCsv}
                    style={{
                        fontFamily: FONT, fontSize: 13, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 14px', border: '1.5px solid ' + C.border, borderRadius: 8,
                        background: C.white, color: C.text, cursor: 'pointer',
                    }}
                >
                    <DownloadIcon /> {t('export_csv')}
                </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                <div className="portal-table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                    <thead style={{ position: 'sticky', top: 0, background: C.white, zIndex: 1 }}>
                        <tr style={{ borderBottom: '1.5px solid ' + C.border }}>
                            <th scope="col" style={{ ...thStyle, cursor: 'pointer' }}
                                aria-sort={sortBy === 'product_name' ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
                                onClick={() => toggleSort('product_name')}>
                                {t('table_product')}{sortArrow('product_name')}
                            </th>
                            <th scope="col" style={{ ...thStyle, cursor: 'pointer' }}
                                onClick={() => toggleSort('category')}>
                                {t('table_category')}{sortArrow('category')}
                            </th>
                            <th scope="col" style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }}
                                onClick={() => toggleSort('units')}>
                                {t('table_units')}{sortArrow('units')}
                            </th>
                            <th scope="col" style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }}
                                onClick={() => toggleSort('revenue')}>
                                {t('table_revenue')}{sortArrow('revenue')}
                            </th>
                            <th scope="col" style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }}
                                onClick={() => toggleSort('revenue_delta_pct')}>
                                Δ%{sortArrow('revenue_delta_pct')}
                            </th>
                            <th scope="col" style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }}
                                onClick={() => toggleSort('margin_pct')}>
                                {t('table_margin')}{sortArrow('margin_pct')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ padding: 40 }}>
                                    <EmptyState title={t('no_matches')} />
                                </td>
                            </tr>
                        ) : (
                            sorted.map((p, i) => (
                                <tr key={p.product_code || i}
                                    style={{ borderBottom: '1px solid ' + C.border }}
                                    onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                    <td style={tdStyle}>
                                        <button
                                            onClick={() => onOpenProduct(p.product_code)}
                                            className="pi-product-btn"
                                            style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                                        >
                                            {p.product_name}
                                        </button>
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12, color: C.muted }}>{p.category || '—'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', ...tabularNumeric }}>{fmtNum(p.units, 0)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, ...tabularNumeric }}>{fmtMoney(p.revenue, currency)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <DeltaBadge value={p.revenue_delta_pct} unit="%" digits={1} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: marginBandColor(p.margin_pct), fontWeight: 600, ...tabularNumeric }}>
                                        {fmtNum(p.margin_pct, 1)}%
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>
        </ModalShell>
    );
}

/* ───── Product detail modal ───── */
function ProductDetailModal({ state, currency, onClose, onNavigateToProduct, t }) {
    const p = state.data;
    const loading = state.loading;

    const chartData = useMemo(() => (p?.daily_trend || []).map(d => ({
        label: formatDateLabel(d.date),
        revenue: Number(d.revenue || 0),
        units: Number(d.units || 0),
    })), [p]);

    return (
        <ModalShell
            widthPx={700}
            onClose={onClose}
            titleNode={
                <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>
                        {p?.product?.name || (loading ? '…' : t('product_detail'))}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                        {p?.product?.category || ''}
                        {p?.product?.subcategory ? ` · ${p.product.subcategory}` : ''}
                    </div>
                </div>
            }
        >
            <div style={{ padding: 20, overflowY: 'auto' }}>
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', fontFamily: FONT, color: C.muted }}>
                        {t('loading')}…
                    </div>
                ) : state.error || !p ? (
                    <EmptyState title={t('failed_to_load')} />
                ) : (
                    <>
                        {/* Mini-KPI tiles */}
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
                            marginBottom: 20,
                        }}>
                            {[
                                { label: t('table_units'), value: fmtNum(p.current.units, 0), prev: p.previous.units, cur: p.current.units, unit: '' },
                                { label: t('table_revenue'), value: fmtMoney(p.current.revenue, currency), prev: p.previous.revenue, cur: p.current.revenue, unit: '' },
                                { label: t('kpi_profit'), value: fmtMoney(p.current.profit, currency), prev: p.previous.profit, cur: p.current.profit, unit: '' },
                                { label: t('kpi_gross_margin'), value: `${fmtNum(p.current.margin_pct, 1)}%`, prev: p.previous.margin_pct, cur: p.current.margin_pct, unit: '' },
                            ].map((tile, i) => {
                                const delta = tile.prev ? ((tile.cur - tile.prev) / Math.abs(tile.prev)) * 100 : null;
                                return (
                                    <div key={i} style={{
                                        background: C.bg, borderRadius: 10, padding: '12px 14px',
                                    }}>
                                        <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted }}>
                                            {tile.label}
                                        </div>
                                        <div style={{ fontFamily: FONT, fontSize: 17, fontWeight: 800, color: C.text, marginTop: 6, ...tabularNumeric }}>
                                            {tile.value}
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <DeltaBadge value={delta} digits={1} unit="%" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Daily trend chart */}
                        <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 10 }}>
                            {t('thirty_day_trend')}
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="pi-detail-fill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={C.green} stopOpacity={0.32} />
                                        <stop offset="100%" stopColor={C.green} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="label" tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }}
                                       axisLine={false} tickLine={false} interval={Math.max(1, Math.floor(chartData.length / 6))} />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                    formatter={(v) => [fmtMoneyPrecise(v, currency), t('table_revenue')]}
                                />
                                <Area type="monotone" dataKey="revenue" stroke={C.green} strokeWidth={2} fill="url(#pi-detail-fill)" />
                            </AreaChart>
                        </ResponsiveContainer>

                        {/* Ranks */}
                        <div style={{
                            marginTop: 16, fontFamily: FONT, fontSize: 13, color: C.muted,
                        }}>
                            {[p.category_rank, p.overall_rank].filter(Boolean).join(' · ')}
                        </div>

                        {/* Footer */}
                        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => onNavigateToProduct(p.product.code)}
                                style={{
                                    fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                    color: C.green, background: 'none', border: '1.5px solid ' + C.green,
                                    borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                                }}
                            >
                                {t('open_in_product_management')} →
                            </button>
                        </div>
                    </>
                )}
            </div>
        </ModalShell>
    );
}
